import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';

const formatDate = (value) => (value ? new Date(value).toLocaleString('ru-RU') : '—');
const dash = (value) => (value === 0 || value ? String(value) : '—');

const Field = ({ label, children }) => (
    <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
        <div className="mt-0.5 text-sm text-slate-800 break-all">{children}</div>
    </div>
);

const UserProfilePage = ({ onShowToast }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [impersonating, setImpersonating] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const r = await fetch(`${API_BASE_URL}/api/users/profile/${id}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!r.ok) throw new Error('Не удалось загрузить профиль');
            setData(await r.json());
            setError(null);
        } catch (e) {
            setError(e.message || 'Ошибка');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [id]);

    const toggleUserBlocked = async () => {
        const nextBlocked = !data.is_blocked;
        const r = await fetch(`${API_BASE_URL}/api/users/${data.id}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ is_blocked: nextBlocked }),
        });
        if (!r.ok) {
            onShowToast?.('Не удалось изменить статус бана');
            return;
        }
        setData((current) => ({ ...current, is_blocked: nextBlocked }));
        onShowToast?.(nextBlocked ? 'Пользователь забанен' : 'Пользователь разбанен');
    };

    const impersonateUser = async () => {
        setImpersonating(true);
        try {
            const r = await fetch(`${API_BASE_URL}/api/users/${data.id}/impersonate`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            const payload = await r.json().catch(() => ({}));
            if (!r.ok) {
                const detail = payload.detail;
                onShowToast?.(typeof detail === 'string' ? detail : 'Не удалось войти в кабинет клиента');
                return;
            }
            if (!payload.url) {
                onShowToast?.('Сервер не вернул ссылку входа');
                return;
            }
            window.open(payload.url, '_blank', 'noopener,noreferrer');
        } catch (e) {
            onShowToast?.(e.message || 'Не удалось войти в кабинет клиента');
        } finally {
            setImpersonating(false);
        }
    };

    if (loading) return <div className="text-slate-500">Загрузка...</div>;
    if (error || !data) return <div className="text-rose-600">{error || 'Профиль не найден'}</div>;

    const bonus = data.bonus || {};

    return (
        <div className="space-y-6">
            <button onClick={() => navigate(-1)} className="text-sm text-indigo-600 hover:underline">← Назад</button>
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="text-2xl font-bold text-slate-800">Профиль клиента</h2>
                    <p className="text-sm text-slate-500 mt-1">
                        {data.fio || 'Без имени'} · ID {data.id}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={impersonateUser}
                        disabled={impersonating || data.is_blocked || data.is_active === false || (data.role && data.role !== 'tg_user')}
                        className="px-4 py-2 rounded-lg text-sm text-white bg-indigo-600 disabled:opacity-40"
                    >
                        {impersonating ? 'Открываем кабинет…' : 'Авторизоваться в ЛК'}
                    </button>
                    <button
                        type="button"
                        onClick={toggleUserBlocked}
                        className={`px-4 py-2 rounded-lg text-sm text-white ${
                            data.is_blocked ? 'bg-emerald-600' : 'bg-rose-600'
                        }`}
                    >
                        {data.is_blocked ? 'Разбанить пользователя' : 'Забанить пользователя'}
                    </button>
                </div>
            </div>

            <div className="bg-white border border-slate-100 rounded-xl p-4">
                <h3 className="font-semibold mb-3">Аккаунт</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Field label="ID">{data.id}</Field>
                    <Field label="ФИО">{dash(data.fio)}</Field>
                    <Field label="Email">{data.email || '—'}</Field>
                    <Field label="Email подтверждён">
                        {data.email_verified ? `Да · ${formatDate(data.email_verified_at)}` : 'Нет'}
                    </Field>
                    <Field label="Пароль">{data.has_password ? 'Задан' : 'Нет'}</Field>
                    <Field label="Роль">{dash(data.role)}</Field>
                    <Field label="Статус">
                        {data.is_blocked ? 'Забанен' : data.is_active === false ? 'Неактивен' : 'Активен'}
                    </Field>
                    <Field label="Капча">{data.captcha_passed ? 'Пройдена' : 'Не пройдена'}</Field>
                    <Field label="Регистрация">{formatDate(data.created_at)}</Field>
                    <Field label="Последний заход">{formatDate(data.last_seen_at)}</Field>
                    <Field label="UTM">{dash(data.utm_label)}</Field>
                </div>
            </div>

            <div className="bg-white border border-slate-100 rounded-xl p-4">
                <h3 className="font-semibold mb-3">Telegram</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Field label="Username">{data.username ? `@${data.username}` : '—'}</Field>
                    <Field label="TG ID">{dash(data.tg_id)}</Field>
                    <Field label="Привязан">{data.tg_id ? 'Да' : 'Нет'}</Field>
                    <Field label="Дата привязки">{formatDate(data.telegram_bound_at)}</Field>
                    <Field label="Ребинд доступен с">{formatDate(data.telegram_rebind_available_at)}</Field>
                </div>
            </div>

            <div className="bg-white border border-slate-100 rounded-xl p-4">
                <h3 className="font-semibold mb-3">Бонусы и реферал</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Field label="Баланс бонусов">{Number(bonus.balance || 0)}</Field>
                    <Field label="Начислено за всё время">{Number(bonus.lifetime_earned || 0)}</Field>
                    <Field label="Потрачено">{Number(bonus.lifetime_spent || 0)}</Field>
                    <Field label="Реферальный баланс">{Number(data.referral_balance || 0).toFixed(2)} ₽</Field>
                    <Field label="Пригласил">
                        {data.referrer ? (
                            <Link className="text-indigo-600 hover:underline" to={`/users/${data.referrer.id}`}>
                                {data.referrer.username ? `@${data.referrer.username}` : data.referrer.fio || data.referrer.id}
                            </Link>
                        ) : (
                            '—'
                        )}
                    </Field>
                </div>
            </div>

            <div className="bg-white border border-slate-100 rounded-xl p-4">
                <h3 className="font-semibold mb-2">Способы входа</h3>
                {!data.identities?.length ? (
                    <p className="text-slate-500 text-sm">Нет привязанных Google / Yandex / email identity</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="border-b text-left text-slate-500">
                                    <th className="py-2 pr-3">Провайдер</th>
                                    <th className="py-2 pr-3">Email</th>
                                    <th className="py-2 pr-3">Подтверждён</th>
                                    <th className="py-2 pr-3">Последний вход</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.identities.map((item) => (
                                    <tr key={`${item.provider}-${item.email || ''}`} className="border-b border-slate-100">
                                        <td className="py-2 pr-3">{item.provider}</td>
                                        <td className="py-2 pr-3">{item.email || '—'}</td>
                                        <td className="py-2 pr-3">{item.email_verified ? 'Да' : 'Нет'}</td>
                                        <td className="py-2 pr-3">{formatDate(item.last_login_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="bg-white border border-slate-100 rounded-xl p-4">
                <h3 className="font-semibold mb-2">Его боты</h3>
                {!data.bots?.length ? <p className="text-slate-500 text-sm">Ботов нет</p> : (
                    <ul className="space-y-1 text-sm">
                        {data.bots.map((b) => (
                            <li key={b.id}>@{b.bot_username || '—'} · наценка {b.margin_percent}% · {b.is_active ? 'active' : 'inactive'}</li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="bg-white border border-slate-100 rounded-xl p-4">
                <h3 className="font-semibold mb-2">История обменов / покупок</h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr className="border-b">
                                <th className="text-left py-2">ID</th>
                                <th className="text-left py-2">Статус</th>
                                <th className="text-left py-2">Сумма</th>
                                <th className="text-left py-2">Кол-во</th>
                                <th className="text-left py-2">Тип</th>
                                <th className="text-left py-2">Источник</th>
                                <th className="text-left py-2">Получатель</th>
                                <th className="text-left py-2">Дата</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data.orders || []).map((o) => (
                                <tr key={o.id} className="border-b border-slate-100">
                                    <td className="py-2">{o.id}</td>
                                    <td>{o.status}</td>
                                    <td>{Number(o.amount || 0).toFixed(2)} ₽</td>
                                    <td>{o.quantity}</td>
                                    <td>{o.purchase_kind || '—'}</td>
                                    <td>{o.order_source || '—'}</td>
                                    <td>{o.recipient === 'friend' ? `@${o.recipient_username || '—'}` : 'Себе'}</td>
                                    <td>{formatDate(o.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default UserProfilePage;
